import { useState, useMemo, useEffect } from "react";
import { 
  useV1ProductionRoutingFamiliesList,
  useV1ProductionRoutingFamiliesCreate,
  useV1ProductionRoutingRevisionsList,
  useV1ProductionRoutingRevisionsCreate,
  useV1ProductionRoutingOperationsList,
  useV1ProductionRoutingOperationsCreate,
  useV1ProductionRoutingOperationsDestroy,
  useV1ProductionWorkCentersList,
  v1ProductionRoutingRevisionsPublishCreate
} from "@/api/generated/v1/v1";
import { DependencyGraphEditor } from "../DependencyGraphEditor";
import { WorkInstructionFamilyListPage } from '../work-instructions/WorkInstructionFamilyListPage';
import { WorkInstructionRevisionEditor } from '../work-instructions/WorkInstructionRevisionEditor';
import { usePermission } from "@/auth/usePermission";
import { useQueryClient } from "@tanstack/react-query";
import { PageBanner } from "@/shared/components/PageBanner";

/**
 * Routing (Manufacturing Pathways) Home Page.
 * Includes Routing Family management, Revision management, and Station/Operation assignment.
 */
export function RoutingHomePage() {
  const canManage = usePermission("production.manage");
  const queryClient = useQueryClient();

  // Queries
  const { data: familiesData, isLoading: familiesLoading, refetch: refetchFamilies } = useV1ProductionRoutingFamiliesList();
  const { data: revisionsData, isLoading: revisionsLoading, refetch: refetchRevisions } = useV1ProductionRoutingRevisionsList();
  const { data: workCentersData, isLoading: wcLoading } = useV1ProductionWorkCentersList();
  
  const createFamily = useV1ProductionRoutingFamiliesCreate();
  const createRevision = useV1ProductionRoutingRevisionsCreate();
  const createOperation = useV1ProductionRoutingOperationsCreate();
  const deleteOperation = useV1ProductionRoutingOperationsDestroy();

  // Local UI states
  const [searchTerm, setSearchTerm] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [newName, setNewName] = useState("");

  // Revision & Station Drawer state
  const [selectedFamily, setSelectedFamily] = useState<any | null>(null);
  const [selectedRevisionId, setSelectedRevisionId] = useState<string | null>(null);

  // Work Instructions Modal state
  const [selectedInstructionOp, setSelectedInstructionOp] = useState<any | null>(null);
  const [selectedInstructionFamily, setSelectedInstructionFamily] = useState<string | null>(null);

  // New Operation Form state
  const [newOpWorkCenter, setNewOpWorkCenter] = useState("");
  const [newOpSequence, setNewOpSequence] = useState("10");
  const [newOpSetupMinutes, setNewOpSetupMinutes] = useState("5");
  const [newOpRunMinutes, setNewOpRunMinutes] = useState("10");

  const [isPublishing, setIsPublishing] = useState(false);

  const families = familiesData?.results || [];
  const revisions = revisionsData?.results || [];
  const workCenters = workCentersData?.results || [];

  // Query operations for selected revision
  const { data: operationsData, refetch: refetchOperations } = useV1ProductionRoutingOperationsList(
    selectedRevisionId ? ({ routing_revision: selectedRevisionId } as any) : undefined,
    { query: { enabled: !!selectedRevisionId } }
  );
  const operations = operationsData?.results || [];

  // Sorted operations by sequence_no ascending
  const sortedOperations = useMemo(() => {
    return [...operations].sort((a: any, b: any) => (Number(a.sequence_no) || 0) - (Number(b.sequence_no) || 0));
  }, [operations]);

  // Auto calculate next sequence number whenever selectedRevisionId or operations change
  useEffect(() => {
    if (selectedRevisionId) {
      if (operations.length > 0) {
        const maxSeq = operations.reduce((max: number, op: any) => Math.max(max, Number(op.sequence_no) || 0), 0);
        setNewOpSequence(String(maxSeq + 10));
      } else {
        setNewOpSequence("10");
      }
    }
  }, [operations, selectedRevisionId]);

  // Revisions for the selected family
  const familyRevisions = useMemo(() => {
    if (!selectedFamily) return [];
    return revisions.filter((r: any) => {
      const famId = (typeof r.family === 'object' ? r.family?.id : r.family);
      return famId === selectedFamily.id;
    });
  }, [revisions, selectedFamily]);

  // Statistics
  const publishedRevisionsCount = useMemo(() => {
    return revisions.filter((r: any) => r.status === "published").length;
  }, [revisions]);

  const filteredFamilies = useMemo(() => {
    if (!searchTerm.trim()) return families;
    const term = searchTerm.toLowerCase();
    return families.filter(
      (f: any) => f.name.toLowerCase().includes(term) || f.code.toLowerCase().includes(term)
    );
  }, [families, searchTerm]);

  const handleCreateFamily = () => {
    if (!newCode.trim() || !newName.trim()) return;
    createFamily.mutate(
      { data: { code: newCode.trim(), name: newName.trim() } as any },
      {
        onSuccess: () => {
          setNewCode("");
          setNewName("");
          setIsCreateOpen(false);
          refetchFamilies();
        },
      }
    );
  };

  const handleCreateRevision = () => {
    if (!selectedFamily) return;
    createRevision.mutate(
      { data: { family: selectedFamily.id, notes: "نسخه جدید سیستم" } as any },
      {
        onSuccess: (res: any) => {
          refetchRevisions();
          refetchFamilies();
          setSelectedRevisionId(res.id);
        },
      }
    );
  };

  const handleAddOperation = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRevisionId || !newOpWorkCenter) return;

    const seqNum = Number(newOpSequence);
    if (!seqNum || seqNum <= 0) {
      alert("لطفاً یک شماره گام معتبر و بزرگتر از صفر وارد کنید.");
      return;
    }

    // Check for duplicate sequence_no in current revision's operations
    const maxSeq = operations.reduce((max: number, op: any) => Math.max(max, Number(op.sequence_no) || 0), 0);
    const isDuplicate = operations.some((op: any) => Number(op.sequence_no) === seqNum);
    if (isDuplicate) {
      alert(`خطا: شماره گام #${seqNum} تکراری است! لطفاً یک شماره گام منحصر به فرد (پیش‌فرض: #${maxSeq + 10}) وارد کنید.`);
      return;
    }

    const selectedWc = workCenters.find((w: any) => w.id === newOpWorkCenter);

    createOperation.mutate(
      {
        data: {
          routing_revision: selectedRevisionId,
          work_center: newOpWorkCenter,
          sequence_no: seqNum,
          setup_minutes: Number(newOpSetupMinutes) || 0,
          run_minutes: Number(newOpRunMinutes) || 0,
          operation_code: `OP-${seqNum}`,
          operation_name: selectedWc?.name || "عملیات ایستگاه کاری",
        } as any,
      },
      {
        onSuccess: () => {
          setNewOpWorkCenter("");
          refetchOperations();
        },
        onError: (err: any) => {
          alert("خطا در افزودن ایستگاه کاری: " + JSON.stringify(err?.response?.data || err?.message));
        }
      }
    );
  };

  const handleDeleteOperation = (opId: string) => {
    if (!confirm("آیا از حذف این ایستگاه کاری اطمینان دارید؟")) return;
    deleteOperation.mutate(
      { id: opId },
      {
        onSuccess: () => {
          refetchOperations();
        },
        onError: (err: any) => {
          alert("خطا در حذف ایستگاه کاری: " + JSON.stringify(err?.response?.data || err?.message));
        }
      }
    );
  };

  const handlePublishRevision = async (revId: string) => {
    try {
      setIsPublishing(true);
      await v1ProductionRoutingRevisionsPublishCreate(revId, {} as any);
      refetchRevisions();
      refetchFamilies();
      queryClient.invalidateQueries({ queryKey: ["v1ProductionRoutingFamiliesList"] });
      alert("نسخه با موفقیت منتشر شد و مسیر فعال گردید! 🚀");
    } catch (err: any) {
      alert("خطا در انتشار نسخه: " + JSON.stringify(err?.response?.data || err?.message));
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <div
      dir="rtl"
      style={{
        padding: "0.5rem 0",
        maxWidth: "100%",
        margin: "0",
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
        fontFamily: "var(--font-family-fa, sans-serif)",
      }}
    >
      <style>{`
        .kpi-card {
          padding: 0.5rem 0.5rem 0.5rem 4rem;
          min-height: 110px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border-radius: 16px;
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 0.1rem;
          position: relative;
          overflow: hidden;
        }
        .kpi-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.15);
          border-color: rgba(0, 114, 112, 0.3);
        }
      `}</style>

      {/* 1. STANDARDIZED PAGE BANNER */}
      <PageBanner
        title="مسیریابی تولید (Routing)"
        subtitle="تعریف و مدیریت ایستگاه‌ها، گام‌های عملیاتی و فرایندهای ساخت محصولات کارخانه"
        icon="🗺️"
        helpLink="/help/routing"
      >
        {canManage && (
          <button
            onClick={() => setIsCreateOpen(true)}
            style={{
              padding: "0.6rem 1.2rem",
              borderRadius: "12px",
              background: "#ffffff",
              color: "#007270",
              fontWeight: "700",
              fontSize: "0.9rem",
              border: "none",
              cursor: "pointer",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              transition: "all 0.2s ease",
            }}
          >
            <span>➕</span>
            <span>تعریف مسیر جدید</span>
          </button>
        )}
      </PageBanner>

      {/* 2. STATS GRID (KPI CARDS) */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "1.5rem",
        }}
      >
        <div className="brand-card kpi-card">
          <span style={{ fontSize: "1.8rem", background: "rgba(0,114,112,0.08)", width: "45px", height: "45px", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            🗺️
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", flex: 1 }}>
            <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "var(--color-text-main, #2B2B2B)" }}>
              خانواده‌های مسیریابی
            </span>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "1.2rem", fontWeight: "800", color: "#007270" }}>
                {familiesLoading ? "..." : families.length}
              </span>
              <span style={{ fontSize: "0.8rem", fontWeight: "600", color: "#10B981" }}>فعال</span>
            </div>
          </div>
        </div>

        <div className="brand-card kpi-card">
          <span style={{ fontSize: "1.8rem", background: "rgba(16,185,129,0.08)", width: "45px", height: "45px", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            📑
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", flex: 1 }}>
            <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "var(--color-text-main, #2B2B2B)" }}>
              نسخه‌های منتشرشده
            </span>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "1.2rem", fontWeight: "800", color: "#10B981" }}>
                {revisionsLoading ? "..." : publishedRevisionsCount}
              </span>
              <span style={{ fontSize: "0.8rem", fontWeight: "600", color: "#10B981" }}>آماده به کار</span>
            </div>
          </div>
        </div>

        <div className="brand-card kpi-card">
          <span style={{ fontSize: "1.8rem", background: "rgba(245,158,11,0.08)", width: "45px", height: "45px", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            🚉
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", flex: 1 }}>
            <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "var(--color-text-main, #2B2B2B)" }}>
              ایستگاه‌های کاری
            </span>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "1.2rem", fontWeight: "800", color: "#F59E0B" }}>
                {wcLoading ? "..." : workCenters.length}
              </span>
              <span style={{ fontSize: "0.8rem", fontWeight: "600", color: "#F59E0B" }}>آماده تخصیص</span>
            </div>
          </div>
        </div>

        <div className="brand-card kpi-card">
          <span style={{ fontSize: "1.8rem", background: "rgba(59,130,246,0.08)", width: "45px", height: "45px", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            ⚡
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", flex: 1 }}>
            <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "var(--color-text-main, #2B2B2B)" }}>
              وضعیت فرآیندها
            </span>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "1.2rem", fontWeight: "800", color: "#3B82F6" }}>
                بهینه‌شده
              </span>
              <span style={{ fontSize: "0.8rem", fontWeight: "600", color: "#3B82F6" }}>زنده</span>
            </div>
          </div>
        </div>
      </div>

      {/* 3. SEARCH & TABLE CONTAINER */}
      <div className="brand-card-extand" style={{ padding: "1.5rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem", flexWrap: "wrap", gap: "1rem" }}>
          <h2 style={{ fontSize: "1.1rem", fontWeight: 800, color: "#007270", margin: 0 }}>
            فهرست مسیرهای تولید (Routing Families)
          </h2>

          <div style={{ position: "relative", minWidth: "260px" }}>
            <input
              type="text"
              placeholder="جستجوی نام یا کد مسیر..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                borderRadius: "10px",
                border: "1px solid var(--color-border, #E4DCC8)",
                fontSize: "0.85rem",
                background: "#FFFFFF",
                color: "#2B2B2B",
                outline: "none",
              }}
            />
          </div>
        </div>

        {familiesLoading ? (
          <div style={{ textAlign: "center", padding: "3rem", color: "#666" }}>
            <p>در حال بارگذاری اطلاعات مسیرهای تولید...</p>
          </div>
        ) : filteredFamilies.length === 0 ? (
          <div style={{ textAlign: "center", padding: "3rem", color: "#666" }}>
            <p style={{ fontWeight: 700 }}>هیچ مسیر تولیدی ثبت نشده است.</p>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", textAlign: "right", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid var(--color-border)", color: "#007270" }}>
                  <th style={{ padding: "0.75rem 1rem", fontWeight: "700" }}>کد مسیر (Routing Code)</th>
                  <th style={{ padding: "0.75rem 1rem", fontWeight: "700" }}>نام فرآیند ساخت</th>
                  <th style={{ padding: "0.75rem 1rem", fontWeight: "700" }}>آخرین نسخه (Revision)</th>
                  <th style={{ padding: "0.75rem 1rem", fontWeight: "700" }}>وضعیت</th>
                  <th style={{ padding: "0.75rem 1rem", fontWeight: "700" }}>عملیات و تعریف ایستگاه‌ها</th>
                </tr>
              </thead>
              <tbody>
                {filteredFamilies.map((family: any) => {
                  // Compute revisions for this family
                  const famRevs = revisions.filter((r: any) => {
                    const famId = (typeof r.family === 'object' ? r.family?.id : r.family);
                    return famId === family.id;
                  });
                  const publishedRevs = famRevs.filter((r: any) => r.status === 'published');
                  publishedRevs.sort((a: any, b: any) => b.revision_no - a.revision_no);
                  
                  const latestRev = publishedRevs[0];
                  const isActive = !!latestRev;

                  return (
                    <tr
                      key={family.id}
                      style={{ borderBottom: "1px solid var(--color-border)", transition: "background 0.2s" }}
                    >
                      <td style={{ padding: "0.75rem 1rem", fontWeight: "700", fontFamily: "monospace", direction: "ltr", textAlign: "right" }}>
                        <span style={{ padding: "2px 8px", borderRadius: "6px", background: "rgba(0,114,112,0.08)", color: "#007270" }}>
                          {family.code}
                        </span>
                      </td>
                      <td style={{ padding: "0.75rem 1rem", fontWeight: "600", color: "#2B2B2B" }}>
                        {family.name}
                      </td>
                      <td style={{ padding: "0.75rem 1rem" }}>
                        {latestRev ? (
                          <span style={{ fontSize: "0.85rem", color: "#007270", fontWeight: 700 }}>
                            نسخه {latestRev.revision_no} ({latestRev.notes || "منتشرشده"})
                          </span>
                        ) : (
                          <span style={{ fontSize: "0.8rem", color: "#888" }}>بدون نسخه منتشرشده</span>
                        )}
                      </td>
                      <td style={{ padding: "0.75rem 1rem" }}>
                        <span
                          style={{
                            padding: "0.25rem 0.6rem",
                            borderRadius: "6px",
                            fontSize: "0.8rem",
                            fontWeight: "700",
                            background: isActive ? "rgba(16, 185, 129, 0.12)" : "rgba(239, 68, 68, 0.12)",
                            color: isActive ? "#10B981" : "#EF4444",
                          }}
                        >
                          {isActive ? "● فعال" : "غیرفعال"}
                        </span>
                      </td>
                      <td style={{ padding: "0.75rem 1rem" }}>
                        <button
                          onClick={() => {
                            setSelectedFamily(family);
                            // Find active revision or default to first
                            const revs = revisions.filter((r: any) => {
                              const famId = (typeof r.family === 'object' ? r.family?.id : r.family);
                              return famId === family.id;
                            });
                            if (revs.length > 0 && revs[0]) {
                              setSelectedRevisionId(revs[0].id);
                            } else {
                              setSelectedRevisionId(null);
                            }
                          }}
                          style={{
                            padding: "0.4rem 0.8rem",
                            borderRadius: "8px",
                            fontSize: "0.8rem",
                            fontWeight: "700",
                            background: "#007270",
                            color: "#FFFFFF",
                            border: "none",
                            cursor: "pointer",
                            boxShadow: "0 2px 6px rgba(0, 114, 112, 0.2)",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          ⚙️ مدیریت ایستگاه‌ها و نسخه‌ها
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 4. CREATE ROUTING FAMILY MODAL */}
      {isCreateOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
        >
          <div
            className="brand-card-extand"
            style={{
              padding: "1.5rem",
              width: "100%",
              maxWidth: "440px",
              boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "0.75rem", marginBottom: "1rem", borderBottom: "1px solid var(--color-border, #E4DCC8)" }}>
              <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: "#007270" }}>🗺️ تعریف مسیر تولید (Routing) جدید</h3>
              <button onClick={() => setIsCreateOpen(false)} style={{ background: "none", border: "none", color: "#888", fontSize: "1.2rem", cursor: "pointer" }}>
                ✕
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
              <div>
                <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>کد مسیر (مثال: RT-MOTOR-01)</label>
                <input
                  type="text"
                  placeholder="RT-MOTOR-01"
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value)}
                  style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none", direction: "ltr" }}
                />
              </div>

              <div>
                <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>نام فرآیند ساخت / Routing</label>
                <input
                  type="text"
                  placeholder="مسیر مونتاژ الکتروموتور اصلی"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none" }}
                />
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "1.25rem", paddingTop: "0.75rem", borderTop: "1px solid var(--color-border, #E4DCC8)" }}>
              <button onClick={() => setIsCreateOpen(false)} style={{ padding: "8px 16px", borderRadius: "10px", background: "none", border: "1px solid var(--color-border, #E4DCC8)", color: "#555", fontSize: "0.85rem", cursor: "pointer" }}>
                انصراف
              </button>
              <button onClick={handleCreateFamily} disabled={createFamily.isPending} style={{ padding: "8px 16px", borderRadius: "10px", background: "#007270", color: "#FFF", border: "none", fontSize: "0.85rem", fontWeight: 700, cursor: "pointer" }}>
                {createFamily.isPending ? "در حال ثبت..." : "ذخیره مسیر جدید"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 5. ROUTING OPERATIONS & REVISIONS MANAGER MODAL */}
      {selectedFamily && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(6px)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
        >
          <div
            className="brand-card-extand"
            style={{
              padding: "1.5rem",
              width: "100%",
              maxWidth: "760px",
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: "0 25px 60px rgba(0,0,0,0.3)",
            }}
          >
            {/* Modal Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "1rem", marginBottom: "1rem", borderBottom: "2px solid var(--color-border, #E4DCC8)" }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <h2 style={{ margin: 0, fontSize: "1.2rem", fontWeight: 800, color: "#007270" }}>
                    مدیریت ایستگاه‌ها و نسخه‌های مسیر: {selectedFamily.name}
                  </h2>
                  <span style={{ padding: "2px 8px", borderRadius: "6px", background: "rgba(0,114,112,0.1)", color: "#007270", fontSize: "0.8rem", fontFamily: "monospace", direction: "ltr" }}>
                    {selectedFamily.code}
                  </span>
                </div>
              </div>
              <button onClick={() => setSelectedFamily(null)} style={{ background: "none", border: "none", color: "#888", fontSize: "1.4rem", cursor: "pointer" }}>
                ✕
              </button>
            </div>

            {/* Revisions Bar & Actions */}
            <div style={{ marginBottom: "1.5rem", padding: "1rem", background: "rgba(0,0,0,0.02)", borderRadius: "12px", border: "1px solid var(--color-border, #E4DCC8)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                <h3 style={{ margin: 0, fontSize: "0.95rem", fontWeight: 800, color: "var(--color-text-main)" }}>
                  نسخه‌های مسیر (Revisions)
                </h3>
                {canManage && (
                  <button
                    onClick={handleCreateRevision}
                    disabled={createRevision.isPending}
                    style={{
                      padding: "4px 10px",
                      borderRadius: "8px",
                      fontSize: "0.8rem",
                      fontWeight: "700",
                      background: "#007270",
                      color: "#FFFFFF",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    + ایجاد نسخه جدید (Draft)
                  </button>
                )}
              </div>

              {familyRevisions.length === 0 ? (
                <div style={{ fontSize: "0.85rem", color: "#666", padding: "8px 0" }}>
                  هیچ نسخه‌ای برای این مسیر ایجاد نشده است. برای تعریف ایستگاه‌ها، ابتدا دکمه «+ ایجاد نسخه جدید» را بزنید.
                </div>
              ) : (
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {familyRevisions.map((rev: any) => {
                    const isSelected = selectedRevisionId === rev.id;
                    const isPublished = rev.status === "published";

                    return (
                      <button
                        key={rev.id}
                        onClick={() => setSelectedRevisionId(rev.id)}
                        style={{
                          padding: "6px 12px",
                          borderRadius: "8px",
                          fontSize: "0.8rem",
                          fontWeight: 700,
                          border: isSelected ? "2px solid #007270" : "1px solid var(--color-border, #E4DCC8)",
                          background: isSelected ? "rgba(0,114,112,0.1)" : "#FFFFFF",
                          color: isSelected ? "#007270" : "#2B2B2B",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <span>نسخه {rev.revision_no}</span>
                        <span style={{ fontSize: "0.7rem", padding: "1px 6px", borderRadius: "4px", background: isPublished ? "rgba(16, 185, 129, 0.15)" : "rgba(245, 158, 11, 0.15)", color: isPublished ? "#10B981" : "#F59E0B" }}>
                          {isPublished ? "Published" : "Draft"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Selected Revision Operations & Add Station Form */}
            {selectedRevisionId ? (() => {
              const currentRev = familyRevisions.find((r: any) => r.id === selectedRevisionId);
              const isDraft = currentRev?.status === "draft";
              const isPublished = currentRev?.status === "published";

              return (
                <div>
                  {/* Header for Active Revision */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", flexWrap: "wrap", gap: "10px" }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 800, color: "#007270" }}>
                        ایستگاه‌های کاری و گام‌های عملیاتی (نسخه {currentRev?.revision_no || "۱"})
                      </h3>
                    </div>
                    {isDraft && canManage && (
                      <button
                        onClick={() => handlePublishRevision(selectedRevisionId)}
                        disabled={isPublishing || operations.length === 0}
                        style={{
                          padding: "6px 14px",
                          borderRadius: "10px",
                          fontSize: "0.85rem",
                          fontWeight: 800,
                          background: "#10B981",
                          color: "#FFFFFF",
                          border: "none",
                          cursor: "pointer",
                          boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
                        }}
                      >
                        🚀 انتشار نسخه و فعال‌سازی مسیر
                      </button>
                    )}
                  </div>

                  {/* Published Revision Warning Banner */}
                  {isPublished && (
                    <div style={{ padding: "0.85rem 1rem", borderRadius: "10px", background: "rgba(212, 175, 55, 0.12)", border: "1px solid #D4AF37", color: "#665000", fontSize: "0.85rem", marginBottom: "1.25rem", lineHeight: "1.6" }}>
                      🔒 <strong>این نسخه منتشر شده و نهایی است (غیرقابل تغییر مستقیم).</strong><br/>
                      برای افزودن، ویرایش یا حذف ایستگاه‌های کاری، روی دکمه <strong>«+ ایجاد نسخه جدید (Draft)»</strong> در بالای همین کادر کلیک کنید تا یک نسخه پیش‌نویس جدید ایجاد شود.
                    </div>
                  )}

                  {/* Add Station / Operation Form (Only visible in Draft mode) */}
                  {isDraft && (
                    <form onSubmit={handleAddOperation} style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "flex-end", marginBottom: "1.25rem", padding: "1rem", background: "#FFFFFF", borderRadius: "12px", border: "1px solid var(--color-border, #E4DCC8)" }}>
                      <div style={{ flex: "1 1 200px" }}>
                        <label style={{ fontSize: "0.8rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>
                          انتخاب ایستگاه کاری (Work Center)
                        </label>
                        <select
                          value={newOpWorkCenter}
                          onChange={(e) => setNewOpWorkCenter(e.target.value)}
                          style={{ width: "100%", padding: "8px 10px", borderRadius: "8px", border: "1px solid var(--color-border, #E4DCC8)", fontSize: "0.85rem", outline: "none", background: "var(--color-surface, #F5F1E6)" }}
                          required
                        >
                          <option value="" disabled>یک ایستگاه کاری انتخاب کنید...</option>
                          {workCenters.map((wc: any) => (
                            <option key={wc.id} value={wc.id}>
                              {wc.name} ({wc.code})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div style={{ width: "90px" }}>
                        <label style={{ fontSize: "0.8rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>
                          شماره گام
                        </label>
                        <input
                          type="number"
                          value={newOpSequence}
                          onChange={(e) => setNewOpSequence(e.target.value)}
                          style={{ width: "100%", padding: "8px 10px", borderRadius: "8px", border: "1px solid var(--color-border, #E4DCC8)", fontSize: "0.85rem", outline: "none" }}
                          required
                          min="1"
                        />
                      </div>

                      <div style={{ width: "120px" }}>
                        <label style={{ fontSize: "0.8rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>
                          زمان آماده‌سازی (دقیقه)
                        </label>
                        <input
                          type="number"
                          value={newOpSetupMinutes}
                          onChange={(e) => setNewOpSetupMinutes(e.target.value)}
                          style={{ width: "100%", padding: "8px 10px", borderRadius: "8px", border: "1px solid var(--color-border, #E4DCC8)", fontSize: "0.85rem", outline: "none" }}
                          min="0"
                        />
                      </div>

                      <div style={{ width: "120px" }}>
                        <label style={{ fontSize: "0.8rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>
                          زمان ساخت (دقیقه)
                        </label>
                        <input
                          type="number"
                          value={newOpRunMinutes}
                          onChange={(e) => setNewOpRunMinutes(e.target.value)}
                          style={{ width: "100%", padding: "8px 10px", borderRadius: "8px", border: "1px solid var(--color-border, #E4DCC8)", fontSize: "0.85rem", outline: "none" }}
                          min="0"
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={createOperation.isPending}
                        style={{
                          padding: "8px 16px",
                          borderRadius: "8px",
                          background: "#007270",
                          color: "#FFFFFF",
                          fontWeight: 700,
                          fontSize: "0.85rem",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        + افزودن ایستگاه به این نسخه
                      </button>
                    </form>
                  )}

                  {/* Operations List Table */}
                  {operations.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "2rem", background: "#FFFFFF", borderRadius: "12px", border: "1px dashed var(--color-border, #E4DCC8)", color: "#666", fontSize: "0.85rem" }}>
                      هیچ ایستگاهی به این نسخه از مسیر افزوده نشده است. {isDraft ? "فرم بالا را تکمیل کنید تا ایستگاه‌ها اضافه شوند." : ""}
                    </div>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", textAlign: "right", borderCollapse: "collapse", background: "#FFFFFF", borderRadius: "12px", border: "1px solid var(--color-border, #E4DCC8)" }}>
                        <thead>
                          <tr style={{ borderBottom: "2px solid var(--color-border)", color: "#007270" }}>
                            <th style={{ padding: "0.75rem 1rem", fontWeight: "700" }}>شماره گام</th>
                            <th style={{ padding: "0.75rem 1rem", fontWeight: "700" }}>ایستگاه کاری متصل</th>
                            <th style={{ padding: "0.75rem 1rem", fontWeight: "700" }}>زمان آماده‌سازی / ساخت</th>
                            <th style={{ padding: "0.75rem 1rem", fontWeight: "700", textAlign: "center" }}>اقدامات</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedOperations.map((op: any) => {
                            const wcObj = workCenters.find((w: any) => w.id === (typeof op.work_center === 'object' ? op.work_center?.id : op.work_center));
                            const wcName = wcObj?.name || op.work_center_name || op.operation_name || "ایستگاه کاری";
                            const wcCode = wcObj?.code || op.work_center_code || "";

                            return (
                              <tr key={op.id} style={{ borderBottom: "1px solid var(--color-border)" }}>
                                <td style={{ padding: "0.75rem 1rem", fontWeight: "800", color: "#D4AF37" }}>
                                  #{op.sequence_no}
                                </td>
                                <td style={{ padding: "0.75rem 1rem", fontWeight: "700", color: "#2B2B2B" }}>
                                  🚉 {wcName} {wcCode && <span style={{ fontSize: "0.75rem", color: "#888", fontFamily: "monospace" }}>({wcCode})</span>}
                                </td>
                                <td style={{ padding: "0.75rem 1rem", fontSize: "0.85rem", color: "#444" }}>
                                  ⏱️ آماده‌سازی: <strong>{op.setup_minutes ?? 0} دقیقه</strong> | ساخت: <strong>{op.run_minutes ?? 0} دقیقه</strong>
                                </td>
                                <td style={{ padding: "0.75rem 1rem", textAlign: "center" }}>
                                  <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                                    <button
                                      onClick={() => setSelectedInstructionOp(op)}
                                      style={{
                                        padding: "4px 8px",
                                        borderRadius: "6px",
                                        background: "rgba(59, 130, 246, 0.1)",
                                        color: "#3B82F6",
                                        border: "1px solid rgba(59, 130, 246, 0.3)",
                                        cursor: "pointer",
                                        fontSize: "0.75rem",
                                        fontWeight: 700,
                                      }}
                                    >
                                      📋 دستورالعمل‌ها
                                    </button>
                                    {isDraft && (
                                      <button
                                        onClick={() => handleDeleteOperation(op.id)}
                                        disabled={deleteOperation.isPending}
                                        style={{
                                          padding: "4px 8px",
                                          borderRadius: "6px",
                                          background: "rgba(239, 68, 68, 0.1)",
                                          color: "#EF4444",
                                          border: "1px solid rgba(239, 68, 68, 0.3)",
                                          cursor: "pointer",
                                          fontSize: "0.75rem",
                                          fontWeight: 700,
                                        }}
                                      >
                                        🗑️ حذف
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Dependency Graph Editor (AM-030) */}
                  {selectedRevisionId && (
                    <div style={{ marginTop: "2rem" }}>
                      <DependencyGraphEditor
                        routingRevisionId={selectedRevisionId as string}
                        operations={sortedOperations.map((op: any) => ({
                          id: String(op.id),
                          operationCode: String(op.operation_code || ""),
                          operationName: String(op.operation_name || ""),
                          sequenceNo: Number(op.sequence_no),
                          upstreamJoinType: op.upstream_join_type || "AND",
                        }))}
                        readOnly={!isDraft}
                      />
                    </div>
                  )}

                </div>
              );
            })() : null}
          </div>
        </div>
      )}

      {/* 6. WORK INSTRUCTIONS MODAL */}
      {selectedInstructionOp && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(6px)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
        >
          <div
            className="brand-card-extand"
            style={{
              padding: "1.5rem",
              width: "100%",
              maxWidth: "800px",
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: "0 25px 60px rgba(0,0,0,0.3)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "1rem", marginBottom: "1rem", borderBottom: "2px solid var(--color-border, #E4DCC8)" }}>
              <div>
                <h2 style={{ margin: 0, fontSize: "1.2rem", fontWeight: 800, color: "#007270" }}>
                  مدیریت دستورالعمل‌های کاری (SOP)
                </h2>
                <div style={{ fontSize: "0.85rem", color: "#666", marginTop: "4px" }}>
                  ایستگاه عملیاتی: {selectedInstructionOp.operation_name || selectedInstructionOp.operation_code || selectedInstructionOp.work_center_name} (گام #{selectedInstructionOp.sequence_no})
                </div>
              </div>
              <button onClick={() => { setSelectedInstructionOp(null); setSelectedInstructionFamily(null); }} style={{ background: "none", border: "none", color: "#888", fontSize: "1.4rem", cursor: "pointer" }}>
                ✕
              </button>
            </div>

            <div className="space-y-8" dir="rtl">
              <WorkInstructionFamilyListPage 
                routingOperationId={selectedInstructionOp.id} 
                onSelectFamily={(id) => setSelectedInstructionFamily(id)}
                selectedFamilyId={selectedInstructionFamily}
              />
              
              {selectedInstructionFamily && (
                <div style={{ background: "rgba(0,0,0,0.02)", padding: "1rem", borderRadius: "10px", border: "1px solid var(--color-border)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                    <h3 className="font-bold text-sm text-[#007270] m-0">ویرایش متن دستورالعمل کاری</h3>
                    <button onClick={() => setSelectedInstructionFamily(null)} style={{ background: "none", border: "none", color: "#888", fontSize: "0.85rem", cursor: "pointer", textDecoration: "underline" }}>بستن ویرایشگر</button>
                  </div>
                  <WorkInstructionRevisionEditor familyId={selectedInstructionFamily} onPublished={() => { alert('نسخه با موفقیت منتشر شد!'); setSelectedInstructionFamily(null); }} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
